import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartAddEvent } from '@theme/events';

/**
 * Component for cart upsells (services and products).
 *
 * @extends {Component}
 */
class CartUpsellsComponent extends Component {
  /**
   * Handles keyboard events for upsell items.
   * @param {object} data - Empty data object
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(data, event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.target.click();
    }
  }

  /**
   * Adds a service/simple product to the cart.
   * @param {{ variantId: string }} data - The variant ID to add.
   * @param {Event} event - The click event.
   */
  async addServiceToCart(data, event) {
    const upsellItem = /** @type {HTMLElement} */ (event.target.closest('.cart-upsell-item'));

    if (!data.variantId) {
      console.error('No variant ID provided for upsell');
      return;
    }

    console.log('➕ Adding upsell to cart:', {
      variantId: data.variantId,
      productId: upsellItem?.dataset.productId,
      productHandle: upsellItem?.dataset.productHandle,
    });

    // Add loading state
    if (upsellItem) {
      upsellItem.classList.add('adding');
      upsellItem.style.pointerEvents = 'none';
    }

    // Get sections to update
    const sectionsToUpdate = this.#getSectionsToUpdate();

    const formData = new FormData();
    formData.append('id', data.variantId);
    formData.append('quantity', '1');
    formData.append('sections', Array.from(sectionsToUpdate).join(','));
    formData.append('sections_url', window.location.pathname);

    try {
      // Retry logic for handling 429 rate limits
      let response;
      let result;
      const maxRetries = 3;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          body: formData,
        });

        if (response.status === 429) {
          if (attempt < maxRetries) {
            const waitTime = Math.pow(2, attempt + 1) * 500; // 1s, 2s, 4s
            console.warn(`Rate limited (429). Retrying in ${waitTime}ms... (attempt ${attempt + 1}/${maxRetries})`);
            await new Promise((resolve) => setTimeout(resolve, waitTime));
            continue;
          }
          console.error('Max retries exceeded due to rate limiting');
          return;
        }

        break;
      }

      result = await response.json();

      console.log('📦 Cart add response:', result);

      if (result.status) {
        // Error occurred
        console.error('Add to cart error:', result.message || result.description);
        return;
      }

      console.log('✅ Upsell added successfully. Cart sections:', Object.keys(result.sections || {}));
      console.log('Cart item added:', {
        id: result.id,
        variant_id: result.variant_id,
        key: result.key,
        title: result.title || result.product_title
      });

      // Dispatch cart update event
      this.dispatchEvent(
        new CartAddEvent({}, 'cart-upsells', {
          source: 'service-upsell',
          itemCount: result.item_count,
          sections: result.sections,
        })
      );

      // Morph sections
      for (const sectionId of sectionsToUpdate) {
        if (result.sections?.[sectionId]) {
          morphSection(sectionId, result.sections[sectionId]);
        }
      }
    } catch (error) {
      console.error('Failed to add upsell to cart:', error);
    } finally {
      // Remove loading state
      if (upsellItem) {
        upsellItem.classList.remove('adding');
        upsellItem.style.pointerEvents = '';
      }
    }
  }

  /**
   * Opens the product upsell modal for variant selection.
   * @param {{ handle: string }} data - The product handle.
   */
  openProductModal(data) {
    if (!data.handle) {
      console.error('No product handle provided');
      return;
    }

    const modal = document.querySelector(`cart-upsell-modal[data-product-handle="${data.handle}"]`);
    if (modal && typeof modal.showDialog === 'function') {
      modal.showDialog();
    } else {
      console.error('Upsell modal not found for product:', data.handle);
    }
  }

  /**
   * Gets all cart-items-component section IDs to update.
   * @returns {Set<string>}
   */
  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sections = new Set();

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sections.add(item.dataset.sectionId);
      }
    });

    return sections;
  }
}

if (!customElements.get('cart-upsells-component')) {
  customElements.define('cart-upsells-component', CartUpsellsComponent);
}
